import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { useWorkspace } from "../../../contexts/WorkspaceContext";
import { useAuth } from "../../../contexts/AuthContext";
import shareAPI from "../../../api/share.api";

const ShareModal = ({ show, handleClose }) => {
  const theme = localStorage.getItem('theme');
  const { threadId, currentHistoryId } = useWorkspace();
  const { } = useAuth(); // For future use if needed
  const token = localStorage.getItem('token');
  
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  
  // Share configuration
  const [shareConfig, setShareConfig] = useState({
    title: '',
    description: '',
    expires_at: null,
    share_settings: {
      show_user_info: true,
      show_timestamps: true,
      show_files: false
    }
  });

  const resetModal = () => {
    setShareUrl('');
    setError('');
    setSuccess('');
    setCopied(false);
    setShowMoreOptions(false);
    setShareConfig({
      title: '',
      description: '',
      expires_at: null,
      share_settings: {
        show_user_info: true,
        show_timestamps: true,
        show_files: false
      }
    });
  };

  useEffect(() => {
    if (!show) {
      resetModal();
    }
  }, [show])

  const createShare = async () => {
    if (!token) {
      setError('Please log in to share sessions');
      return;
    }

    if (!currentHistoryId && !threadId) {
      setError('No active session to share');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const sessionId = currentHistoryId || threadId;
      const response = await shareAPI.createSharedSession(sessionId, shareConfig, token);
      
      if (response.success) {
        const fullShareUrl = `${window.location.origin}/share/${response.data.share_token}`;
        setShareUrl(fullShareUrl);
        setSuccess('Share created successfully!');
      } else {
        setError('Failed to create share. Please try again.');
      }
    } catch (err) {
      console.error('Share creation failed:', err);
      setError(err.response?.data?.message || 'Failed to create share. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Error copying text to clipboard', err);
        setError('Failed to copy URL. Please copy manually.');
      });
  };

  const toggleMoreOptions = () => {
    setShowMoreOptions(!showMoreOptions);
  };

  const shareLinkedIn = () => {
    if (!shareUrl) return;
    const encodedLink = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(shareConfig.title || 'Check out this conversation on ModalX');
    const summary = encodeURIComponent(shareConfig.description || 'Interesting conversation shared from ModalX platform');
    window.open(`https://www.linkedin.com/feed/?linkOrigin=LI_BADGE&shareActive=true&shareUrl=${encodedLink}&title=${title}&summary=${summary}`);
  };
  
  const shareSlack = () => {
    if (!shareUrl) return;
    window.open(`https://slack.com/intl/en-in/`);
  };

  const shareTwitter = () => {
    if (!shareUrl) return;
    const text = encodeURIComponent(shareConfig.title || 'Check out this conversation on ModalX');
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${text}`);
  };
  
  const shareFacebook = () => {
    if (!shareUrl) return;
    const encodedLink = encodeURIComponent(shareUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`);
  };
  

  return (
    <Modal show={show} onHide={() => { handleClose(); resetModal(); }} centered size="lg">
      <Modal.Header closeButton style={{ backgroundColor: theme === 'dark' ? '#2d2d2d' : '', color: theme === 'dark' ? 'white' : '' }}>
        <Modal.Title>Share Session</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: theme === 'dark' ? '#1a1a1a' : '', color: theme === 'dark' ? 'white' : '' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        {!shareUrl ? (
          // Configuration form
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Give your shared session a title"
                value={shareConfig.title}
                onChange={(e) => setShareConfig(prev => ({ ...prev, title: e.target.value }))}
                style={{ backgroundColor: theme === 'dark' ? '#2d2d2d' : '', color: theme === 'dark' ? 'white' : '', borderColor: theme === 'dark' ? '#555' : '' }}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add a description to help others understand what this session is about"
                value={shareConfig.description}
                onChange={(e) => setShareConfig(prev => ({ ...prev, description: e.target.value }))}
                style={{ backgroundColor: theme === 'dark' ? '#2d2d2d' : '', color: theme === 'dark' ? 'white' : '', borderColor: theme === 'dark' ? '#555' : '' }}
              />
            </Form.Group>

            <hr style={{ borderColor: theme === 'dark' ? '#555' : '' }} />
            
            <h6>Privacy Settings</h6>
            <Form.Check
              type="checkbox"
              label="Show user information"
              checked={shareConfig.share_settings.show_user_info}
              onChange={(e) => setShareConfig(prev => ({
                ...prev,
                share_settings: { ...prev.share_settings, show_user_info: e.target.checked }
              }))}
              className="mb-2"
            />
            
            <Form.Check
              type="checkbox"
              label="Show timestamps"
              checked={shareConfig.share_settings.show_timestamps}
              onChange={(e) => setShareConfig(prev => ({
                ...prev,
                share_settings: { ...prev.share_settings, show_timestamps: e.target.checked }
              }))}
              className="mb-2"
            />
            
            <Form.Check
              type="checkbox"
              label="Show uploaded files"
              checked={shareConfig.share_settings.show_files}
              onChange={(e) => setShareConfig(prev => ({
                ...prev,
                share_settings: { ...prev.share_settings, show_files: e.target.checked }
              }))}
              className="mb-3"
            />

            <div className="text-center">
              <Button 
                variant="primary" 
                onClick={createShare} 
                disabled={loading || (!currentHistoryId && !threadId)}
                style={{ minWidth: '150px' }}
              >
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Share Link'
                )}
              </Button>
            </div>
          </Form>
        ) : (
          // Share link display
          <div>
            <p className="mb-3">🎉 Your session has been shared! Anyone with this link can view the conversation.</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '90%',
                height: '40px',
                backgroundColor: theme === 'dark' ? '#2d2d2d' : '#dee2e6',
                borderRadius: '15px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {shareUrl}
              </div>
              <div style={{ position: 'relative' }}>
                <button
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    position: 'relative',
                    color: 'blue',
                    transform: hovered ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                  onClick={copyToClipboard}
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                >
                  <i className="bi bi-copy"></i>
                </button>
                {copied && (
                  <div style={{
                    position: 'absolute',
                    top: '-30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'black',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    whiteSpace: 'nowrap',
                    zIndex: 10
                  }}>
                    Copied to clipboard!
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      {shareUrl && (
        <Modal.Footer style={{ backgroundColor: theme === 'dark' ? '#2d2d2d' : '', borderTop: theme === 'dark' ? '1px solid #555' : '' }}>
          <div className="d-flex justify-content-center w-100">
            <Button style={{ borderRadius: '50%', border: 'none', margin: '0 5px' }} variant="outline-primary" onClick={shareLinkedIn}>
              <i className="bi bi-linkedin"></i>
            </Button>
            <Button style={{ borderRadius: '50%', border: 'none', margin: '0 5px' }} variant="outline-primary" onClick={shareTwitter}>
              <i className="bi bi-twitter-x"></i>
            </Button>
            <Button style={{ borderRadius: '50%', border: 'none', margin: '0 5px' }} variant="outline-primary" onClick={shareFacebook}>
              <i className="bi bi-facebook"></i>
            </Button>
            {!showMoreOptions && 
            <Button
              style={{ borderRadius: '50%', border: 'none', margin: '0 5px' }}
              variant="outline-primary"
              onClick={toggleMoreOptions}
            >
              <i className="bi bi-three-dots"></i>
            </Button>}
            {showMoreOptions && (
              <>
                <Button style={{ borderRadius: '50%', border: 'none', margin: '0 5px' }} variant="outline-primary" onClick={shareSlack}>
                  <i className="bi bi-slack"></i>
                </Button>
                <Button style={{ borderRadius: '50%', border: 'none', margin: '0 5px' }} variant="outline-primary">
                  <i className="bi bi-instagram"></i>
                </Button>
              </>
            )}
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ShareModal;
